/** @file MenuBar: File, Edit, View and Help above the top bar; opens by click, hover or Alt chords. */
import { chordLabel } from '@zilo/pose3d-shortcuts/chordLabel';
import { useMemo } from 'react';
import type { ReactElement } from 'react';
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import type { MenuName } from '../state/editorTypes';
import { useEditor } from '../state/useEditor';
import { useEditorStore } from '../state/useEditorStore';
import { chordResolver } from './chordResolver';
import { editMenu } from './editMenu';
import { fileMenu } from './fileMenu';
import { helpMenu } from './helpMenu';
import { MenuDropdown } from './MenuDropdown';
import type { MenuBuilder } from './menuTypes';
import { PLATFORM } from './platform';
import { useApi } from './useApi';
import { viewMenu } from './viewMenu';

const MENUS: { name: MenuName; label: string; build: MenuBuilder }[] = [
  { name: 'file', label: 'File', build: fileMenu }, { name: 'edit', label: 'Edit', build: editMenu },
  { name: 'view', label: 'View', build: viewMenu }, { name: 'help', label: 'Help', build: helpMenu },
];

/** Every key table the chords are looked up in, and the user's overrides. */
export type MenuBarProps = { bindingTables: Binding[][]; overrides: Binding[] };

/**
 * A 26 px strip. The open menu lives in `shell.menuBar` (so the Alt chords can open it); a backdrop under the bar
 * closes it on an outside click and Escape closes it through the shell's `app.escape` binding. Each row shows the
 * chord its binding has now (overrides applied), looked up by binding id in `bindingTables`.
 */
export function MenuBar({ bindingTables, overrides }: MenuBarProps): ReactElement {
  const store = useEditorStore();
  const api = useApi();
  const open = useEditor((s) => s.shell.menuBar);
  const chordOf = useMemo(() => chordResolver(bindingTables, overrides, PLATFORM), [bindingTables, overrides]);
  const set = (menuBar: MenuName | null): void => { store.getState().setShell({ menuBar }); };
  return (
    <>
      {open === null ? null : <div className="p3-menu-backdrop" data-testid="menubar-backdrop" onClick={() => { set(null); }} />}
      <nav className="p3-menubar" role="menubar" aria-label="Menu">
        {MENUS.map((m) => {
          const alt = chordOf(`menu.${m.name}`);
          return (
            <span key={m.name} className="p3-menubar-slot">
              <button type="button" role="menuitem" aria-haspopup="menu" aria-expanded={open === m.name} aria-label={m.label}
                className={open === m.name ? 'p3-menubar-btn p3-menubar-open' : 'p3-menubar-btn'}
                title={alt === undefined ? m.label : `${m.label} (${chordLabel(alt, PLATFORM)})`}
                onClick={() => { set(open === m.name ? null : m.name); }}
                onMouseEnter={() => { if (open !== null && open !== m.name) set(m.name); }}>{m.label}</button>
              {open === m.name ? <MenuDropdown label={m.label} build={m.build} ctx={{ store, api, chordOf }} onPick={() => { set(null); }} /> : null}
            </span>
          );
        })}
      </nav>
    </>
  );
}
